import React, { useCallback, useEffect, useState } from 'react';
import { Clock, RefreshCw, Users, ChevronDown } from 'lucide-react';
import * as gamesApi from '../../api/games';
import { getGameProgress } from '../../api/gameProgress';
import './gameDetails.css';

/**
 * Live round: the pick screen's fixture cards, kept on screen once picks lock, with the score
 * on each and who backed each side (backlog item 75).
 *
 * The pick screen goes when a round locks, which is exactly when the round gets interesting,
 * and people liked that layout. So this borrows its cards rather than its component:
 * `FixtureSelection` is the pick flow, and teaching it to be read-only as well would leave a
 * confirm button one prop away from a locked round.
 *
 * Two sources, both the server's:
 *
 * The score is the result the admin entered in the console, nothing more live than that. Each
 * result takes effect as it is entered, so the cards fill in fixture by fixture over a weekend.
 * It is display only - nobody is eliminated by anything on this screen.
 *
 * Who is on each team comes from Game Progress, which carries a round's picks only once it has
 * locked. That is what makes listing them here safe: the concealment is in the response, not in
 * whether this screen chooses to draw it. "Through" and "out" are the server's outcome for each
 * pick, never worked out here from the scoreline.
 */
export default function LiveRound({ gameId, gameWeek }) {
  const [fixtures, setFixtures] = useState([]);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [fixturesData, progress] = await Promise.all([
        gamesApi.getFixtures(gameId, gameWeek),
        getGameProgress(gameId),
      ]);
      setFixtures(fixturesData || []);
      const rounds = progress?.rounds || [];
      setRound(rounds.find(r => r.gameWeek === gameWeek) ?? rounds.find(r => r.current) ?? null);
    } catch (err) {
      setError(err.name === 'ApiError'
        ? 'Could not load the live round.'
        : 'Could not reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [gameId, gameWeek]);

  useEffect(() => { load(); }, [load]);

  const backers = (teamId) => (round?.entries || []).filter(e => e.teamId === teamId);

  return (
    <div className="fx-root lr-root">
      <div className="fx-heading lr-heading">
        <div>
          <h2>Week {gameWeek} - live</h2>
          <p>Scores appear as each result comes in. Tap a team to see who picked it.</p>
        </div>
        <button className="lr-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'fx-spin' : undefined} /> Refresh
        </button>
      </div>

      {error && <p className="lr-error" role="alert">{error}</p>}

      <div className="fx-list">
        {fixtures.map((entry) => {
          const { homeTeam, awayTeam } = entry;
          if (!homeTeam || !awayTeam) return null;
          const openHere = open?.entryId === entry.id ? open.teamId : null;
          const shownTeam = openHere === homeTeam.id ? homeTeam : openHere === awayTeam.id ? awayTeam : null;

          return (
            <div key={entry.id} className="fx-card lr-card">
              <div className="fx-time">
                <Clock size={14} />
                <span>{statusLine(entry)}</span>
              </div>
              <div className="fx-teams lr-teams">
                <TeamSide
                  team={homeTeam}
                  count={backers(homeTeam.id).length}
                  expanded={openHere === homeTeam.id}
                  onToggle={() => setOpen(openHere === homeTeam.id ? null : { entryId: entry.id, teamId: homeTeam.id })}
                />
                <div className={`fx-vs lr-score${entry.resulted ? ' is-final' : ''}`}>
                  {scoreText(entry)}
                </div>
                <TeamSide
                  team={awayTeam}
                  count={backers(awayTeam.id).length}
                  expanded={openHere === awayTeam.id}
                  onToggle={() => setOpen(openHere === awayTeam.id ? null : { entryId: entry.id, teamId: awayTeam.id })}
                />
              </div>
              {shownTeam && (
                <ul className="lr-backers" aria-label={`Players on ${shownTeam.name}`}>
                  {backers(shownTeam.id).map(e => (
                    <li key={e.playerId} className={e.you ? 'is-you' : undefined}>
                      <span className="lr-backer-name">{e.displayName}{e.you ? ' (you)' : ''}</span>
                      {outcomeLabel(e.outcome) && (
                        <span className={`lr-outcome is-${e.outcome.toLowerCase()}`}>{outcomeLabel(e.outcome)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamSide({ team, count, expanded, onToggle }) {
  return (
    <div className="lr-side">
      <span className="fx-team-name">{team.name}</span>
      {count > 0 ? (
        <button className="lr-count" onClick={onToggle} aria-expanded={expanded}>
          <Users size={13} /> {count} {count === 1 ? 'player' : 'players'}
          <ChevronDown size={13} className={`fx-form-caret${expanded ? ' is-open' : ''}`} aria-hidden="true" />
        </button>
      ) : (
        <span className="lr-count is-none">No picks</span>
      )}
    </div>
  );
}

function scoreText(entry) {
  if (entry.voided) return 'Void';
  if (entry.resulted && entry.homeScore != null && entry.awayScore != null) {
    return `${entry.homeScore} - ${entry.awayScore}`;
  }
  return 'vs';
}

function statusLine(entry) {
  if (entry.voided) return 'Void';
  if (entry.resulted) return 'Full time';
  if (!entry.fixtureDateTime) return 'Kick-off TBC';
  const kickOff = new Date(entry.fixtureDateTime);
  if (kickOff <= new Date()) return 'Kicked off - awaiting result';
  return `${kickOff.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} • ${kickOff.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function outcomeLabel(outcome) {
  if (outcome === 'SURVIVED') return 'Through';
  if (outcome === 'LOST_A_LIFE') return 'Lost a life';
  if (outcome === 'ELIMINATED') return 'Out';
  if (outcome === 'VOIDED') return 'Void';
  return null;
}
